<script lang="ts">
  /** カード種別ごとのコンポーネントを選ぶ（card-schema.md §3 の 8 種と §3.9 の memo） */
  import type { Card } from '../../lib/cards';
  import type { GradeResult } from '../types';
  import {
    asChoiceAnswer,
    asFillBlankAnswer,
    asMemoAnswer,
    asParsonsAnswer,
    asPredictAnswer,
    asVizStepAnswer,
    asWriteAcAnswer,
    type CardAnswer,
  } from './answer';
  import ChoiceCard from './ChoiceCard.svelte';
  import FillBlankCard from './FillBlankCard.svelte';
  import MemoCard from './MemoCard.svelte';
  import ParsonsCard from './ParsonsCard.svelte';
  import PredictOutputCard from './PredictOutputCard.svelte';
  import VizStepCard from './VizStepCard.svelte';
  import WriteACCard from './WriteACCard.svelte';

  interface Props {
    card: Card;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: CardAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    /** write-ac だけ: 採点後にコードを直したら再採点できる（WriteACCard の allowRegrade） */
    allowRegrade?: boolean;
    ongrade?: ((result: GradeResult, answer: CardAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（CardShell の onhint。level は 1 始まり） */
    onhint?: ((level: number) => void) | undefined;
    /** write-ac だけ: 書きかけのコード（WriteACCard の draft） */
    draft?: string | undefined;
    /** write-ac だけ: 書きかけの保存先（WriteACCard の ondraft） */
    ondraft?: ((code: string) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer,
    showUseCase = false,
    globalKeys = false,
    allowRegrade = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
    draft,
    ondraft,
  }: Props = $props();
</script>

{#if card.type === 'predict-output'}
  <PredictOutputCard
    {globalKeys}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asPredictAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
  />
{:else if card.type === 'fill-blank'}
  <FillBlankCard
    {globalKeys}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asFillBlankAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
  />
{:else if card.type === 'parsons'}
  <ParsonsCard
    {globalKeys}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asParsonsAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
  />
{:else if card.type === 'choice' || card.type === 'reduce' || card.type === 'complexity-quiz'}
  <ChoiceCard
    {globalKeys}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asChoiceAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
  />
{:else if card.type === 'write-ac'}
  <WriteACCard
    {globalKeys}
    {allowRegrade}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asWriteAcAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
    {draft}
    {ondraft}
  />
{:else if card.type === 'memo'}
  <MemoCard
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asMemoAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
  />
{:else if card.type === 'viz-step'}
  <VizStepCard
    {globalKeys}
    {card}
    {index}
    {total}
    {box}
    {result}
    answer={asVizStepAnswer(answer)}
    {showUseCase}
    {ongrade}
    {onnext}
    {onprev}
    {onabort}
    {onhint}
  />
{/if}
